/**
 * The administration workspaces the Platform nav group carries today, and
 * WHERE EACH ONE BELONGS (#2328 §1, ROOT ruling 74071 §6).
 *
 * Ruling 74071 §6, verbatim: *"The current Platform group is not grandfathered.
 * Classification, not its present group name, decides where each destination
 * lives"* — thirteen of its fourteen destinations move into registered Settings
 * Studio categories, the Approval Inbox stays a business/work destination, the
 * empty group is removed rather than preserved as chrome, and *"no destination
 * appears in both business navigation and Settings Studio"*.
 *
 * ── WHY THE LIST IS HERE AND NOT DERIVED FROM `Nav.tsx` ────────────────────
 * The fourteen are declared inline in `builtinNav` (`Nav.tsx`, the
 * `groups.push({ label: 'Platform', ... })` block). Importing that function
 * into the registry's runtime graph would pull the whole navigation module —
 * React, the route tree, the cascade — in behind a settings index, and would
 * make the registry go EMPTY the moment B2 deletes the group, which is the one
 * change this file exists to survive.
 *
 * So the destinations live here, and the drift a second list normally costs is
 * paid by a test instead: `workspaces.parity.test.ts` asserts this list and the
 * rendered Platform group are the SAME SET, in both directions, for as long as
 * the group exists. That test is the only thing that makes a second list safe,
 * and it is why this list may not be edited without it.
 * `settingsNavigation.ts:86-97` is the precedent read the other way round: the
 * first fix for the duplicate-settings-navigation defect was itself a
 * hand-maintained restatement, where two lists *"agreed only as long as
 * somebody remembered they had to"*. Here something remembers.
 *
 * When B2 removes the Platform group, the Nav half of that test goes with it
 * and this file becomes the single authority — which is the end state ruling
 * §6 describes.
 *
 * ── THESE ARE NOT `settingsDestinations()` ─────────────────────────────────
 * `settingsDestinations()` is the `/settings?tab=` authority, and the shell's
 * own Settings nav group reads it. A workspace added there would appear in
 * BUSINESS navigation, which is exactly what ruling §6 forbids. The two sets
 * are composed in `pages.ts` and stay separate everywhere else.
 *
 * ── THE CLASSIFICATION IS A JUDGEMENT, IT IS TOTAL, AND IT RAISES ──────────
 * Skill rule 42's inverse: nothing in the tree can compute whether `/personas`
 * belongs under "Navigation & personas" or "Identity & access". #2328 §5's
 * information architecture is the only authority for that answer, so each
 * destination carries its category with the reason written beside it — and a
 * destination nobody has classified RAISES rather than defaulting into a
 * heading no administrator would look under.
 *
 * `business` is a first-class answer, not an absence: the Approval Inbox is a
 * queue of decisions somebody owes — skill rule 23's "work, not news" — and a
 * queue reachable only by opening a settings overlay is a queue nobody empties.
 */
import type { SettingsCategory } from './categories'

/** One destination the Platform nav group carries today. */
export interface PlatformWorkspace {
  /** The route, exactly as `builtinNav` and `router.tsx` declare it. Identity. */
  to: string
  /** The label the Platform group renders today. */
  label: string
  /** Rendered only on a fleet deployment (`builtinNav`'s `fleet` predicate). */
  fleetOnly?: boolean
}

/**
 * Where a Platform destination belongs once the group is gone.
 *
 * `studio` carries the #2328 §5 category and the synonym tails it is findable
 * under; `business` carries the reason it stays a work destination.
 */
export type WorkspacePlacement =
  | {
    kind: 'studio'
    category: SettingsCategory
    keywords: readonly string[]
    /** Used instead of the nav label where ruling §6 names it differently. */
    studioLabel?: string
    reason: string
  }
  | { kind: 'business'; reason: string }

/**
 * The Platform group as `builtinNav` renders it, in document order.
 *
 * Pinned against the live bar by `workspaces.parity.test.ts`;
 * `Nav.builtin.test.ts:89-102` pins the same fourteen lines from the other side.
 */
export const PLATFORM_WORKSPACES: readonly PlatformWorkspace[] = [
  { to: '/ops/setup', label: 'Setup' },
  { to: '/modules', label: 'Modules' },
  { to: '/templates', label: 'Template Studio' },
  { to: '/approvals', label: 'Approvals' },
  { to: '/approvals/inbox', label: 'Approval Inbox' },
  { to: '/approvals/templates', label: 'Template Designer' },
  { to: '/ops/scheduler', label: 'Scheduler & Relay' },
  { to: '/ops/environments', label: 'Environments', fleetOnly: true },
  { to: '/flows', label: 'Flows' },
  { to: '/rules', label: 'Rules' },
  { to: '/access', label: 'People & Access' },
  { to: '/personas', label: 'Personas' },
  { to: '/my-org', label: 'My Organization' },
  { to: '/ui', label: 'Design system' },
]

/**
 * The placement of every destination above — TOTAL, and every answer argued.
 *
 * Read the reasons as the record of a judgement rather than as decoration: the
 * next person to disagree with one of these needs to know what it was weighed
 * against, and one of them below was genuinely close.
 */
export const WORKSPACE_PLACEMENT: Readonly<Record<string, WorkspacePlacement>> = {
  /* Setup Cockpit is "one guided, resumable path from an installed deployment
     to a usable one" (`SetupCockpit.tsx:2-4`) — it brings THIS environment from
     installed to working, beside Deployment and Environments. */
  '/ops/setup': {
    kind: 'studio',
    category: 'environments',
    keywords: ['setup', 'onboarding', 'checklist'],
    reason: 'brings this environment from installed to usable',
  },
  /* What this environment is MADE OF. Deliberately not beside Licence, which
     stays under Operations: a licence is the commercial entitlement, an install
     is the environment's composition, and the ALM comparison on
     /ops/environments reads the installed set as part of the environment. */
  '/modules': {
    kind: 'studio',
    category: 'environments',
    keywords: ['modules', 'install', 'solutions', 'packages'],
    reason: 'the installed composition of this environment, not its entitlement',
  },
  /* Email and document templates are how the deployment PRESENTS itself
     (`TemplateStudio.tsx:1-14`), which is §5's first heading. It would have gone
     under "Notifications & email" — the category ruling 74071 §4 omits until a
     real outbound-email destination exists — and Experience is where a rendered
     artefact's look is governed. */
  '/templates': {
    kind: 'studio',
    category: 'experience',
    keywords: ['template', 'email', 'document', 'publish'],
    reason: 'how this deployment presents itself in documents and mail',
  },
  /* Approval POLICY: who must sign off on what. Governance of work, audited —
     §5's "Operations & audit". The label is ruling 74071 §6's own word for it,
     and it earns the override below because the INBOX stays in business
     navigation under a name one word away. */
  '/approvals': {
    kind: 'studio',
    category: 'operations',
    keywords: ['approval', 'policy', 'governance', 'delegation'],
    studioLabel: 'Approvals configuration',
    reason: 'approval policy is governance of work, and the inbox keeps the plain name',
  },
  /* The one destination ruling 74071 §6 keeps in business navigation. */
  '/approvals/inbox': {
    kind: 'business',
    reason: 'a queue of decisions somebody owes: work, not configuration (ruling 74071 §6)',
  },
  /* The designer for the approval templates that policy uses. It sits with its
     policy rather than with Template Studio: what it authors is an approval
     stage, not a rendered document. */
  '/approvals/templates': {
    kind: 'studio',
    category: 'operations',
    keywords: ['approval', 'template', 'designer', 'stage'],
    reason: 'authors the approval stages the policy beside it enforces',
  },
  /* THE CLOSE ONE. Two honest readings: it is an ops surface ("did the motor
     turn?", `SchedulerOpsPage.tsx:2`, with a dead-letter requeue act), and it is
     where this deployment's outbound webhook egress to other systems is
     watched. Integrations wins on FINDABILITY, which is what #2328 is for: an
     administrator hunting a webhook that will not fire looks under the heading
     that names the outside world. If a subscription-AUTHORING page ever lands it
     belongs on the same shelf, which is the tell that the shelf is right. */
  '/ops/scheduler': {
    kind: 'studio',
    category: 'integrations',
    keywords: ['scheduler', 'jobs', 'webhook', 'relay', 'egress'],
    reason: 'the outbound egress of this deployment and the clock that drives it',
  },
  /* Verbatim §5: "Data & environments". Fleet-gated, exactly as the nav bar
     gates it — a customer box has no ALM chain to compare against. */
  '/ops/environments': {
    kind: 'studio',
    category: 'environments',
    keywords: ['environments', 'promotion', 'snapshot', 'drift'],
    reason: 'the place of this deployment in an ALM chain',
  },
  /* Flows and Rules are one shelf, and `Nav.tsx` already says why they sit
     together: "both are how a deployment's behaviour is authored and put live".
     §5 has no Automation heading; authoring behaviour and putting it live is an
     operation of running the deployment, and activation is audited. */
  '/flows': {
    kind: 'studio',
    category: 'operations',
    keywords: ['flows', 'automation', 'activation', 'simulation'],
    reason: 'authoring the behaviour of this deployment and putting it live',
  },
  '/rules': {
    kind: 'studio',
    category: 'operations',
    keywords: ['rules', 'policy', 'guard', 'routing'],
    reason: 'business policy authored as data, beside Flows for the same reason',
  },
  /* Verbatim §5: "Identity & access". Users, roles, teams, privileges. */
  '/access': {
    kind: 'studio',
    category: 'identity',
    keywords: ['users', 'roles', 'teams', 'privileges', 'permissions'],
    reason: 'who exists and what they may do',
  },
  /* Verbatim §5: "Navigation & personas" — and `Nav.tsx` gives the argument for
     the split from People & Access: "roles gate privilege and live there;
     personas shape experience. Two axes, never mixed (#451)." */
  '/personas': {
    kind: 'studio',
    category: 'navigation',
    keywords: ['personas', 'experience', 'audience'],
    reason: 'personas shape the experience; roles gate privilege and live next door',
  },
  /* Org membership self-service (`MyOrgPage.tsx:1-4`): who belongs to this
     organization and at what membership. That is the question People & Access
     answers one scope out, so it belongs on the same shelf. */
  '/my-org': {
    kind: 'studio',
    category: 'identity',
    keywords: ['organization', 'members', 'membership'],
    reason: 'who belongs to this organization, one scope in from People & Access',
  },
  /* The design-system gallery: the components and tokens the deployment's
     appearance is made of. §5's "Experience & branding". */
  '/ui': {
    kind: 'studio',
    category: 'experience',
    keywords: ['design', 'components', 'gallery', 'tokens'],
    reason: 'the components and tokens the appearance of this deployment is made of',
  },
}

/**
 * The placement of one destination, or a RAISE.
 *
 * Total by construction and loud by choice: a Platform destination added
 * without a placement would otherwise land in whatever the caller's default
 * was, and a settings page under the wrong heading is unfindable with every
 * test still green.
 */
export function classifyPlatformWorkspace(workspace: PlatformWorkspace): WorkspacePlacement {
  const placement = WORKSPACE_PLACEMENT[workspace.to]
  if (placement === undefined) {
    throw new Error(
      `classifyPlatformWorkspace: "${workspace.to}" (${workspace.label}) has no placement. `
      + 'Ruling 74071 §6 classifies every platform destination as a Settings Studio '
      + 'category or as a business/work destination — add it to WORKSPACE_PLACEMENT '
      + 'with the reason, rather than letting it fall into a default nobody chose.',
    )
  }
  return placement
}

/**
 * The page key for a workspace route: `/approvals/templates` -> `approvals-templates`.
 *
 * Generated from the route rather than typed beside it (#2328 §6: *"stable IDs
 * and generated uniqueness checks"*), so two definitions on one route collide
 * instead of differing by a character, and a destination that moves route
 * cannot keep an id pointing at where it used to be.
 */
export function workspaceKey(to: string): string {
  const key = to.replace(/^\//, '').replaceAll('/', '-')
  if (!/^[a-z][a-z0-9]*(?:-[a-z0-9]+)*$/.test(key)) {
    throw new Error(`workspaceKey("${to}"): "${key}" is not a stable page key`)
  }
  return key
}

/** A workspace as the settings page set consumes it: key, label, route. */
export interface WorkspaceDestination {
  key: string
  label: string
  to: string
}

/** The destinations that move INTO the Settings Studio, in document order. */
export function studioWorkspaceDestinations(fleet = false): WorkspaceDestination[] {
  const studio: WorkspaceDestination[] = []
  for (const workspace of PLATFORM_WORKSPACES) {
    if (workspace.fleetOnly === true && !fleet) continue
    const placement = classifyPlatformWorkspace(workspace)
    if (placement.kind !== 'studio') continue
    studio.push({
      key: workspaceKey(workspace.to),
      label: placement.studioLabel ?? workspace.label,
      to: workspace.to,
    })
  }
  return studio
}

/** The destinations that STAY in business navigation. */
export function businessWorkspaceDestinations(fleet = false): PlatformWorkspace[] {
  return PLATFORM_WORKSPACES.filter((workspace) =>
    (fleet || workspace.fleetOnly !== true)
    && classifyPlatformWorkspace(workspace).kind === 'business')
}

/** The #2328 §5 category a studio workspace is classified into, or a RAISE. */
export function workspaceCategory(key: string): SettingsCategory {
  const workspace = PLATFORM_WORKSPACES.find((candidate) => workspaceKey(candidate.to) === key)
  if (workspace === undefined) {
    throw new Error(`workspaceCategory("${key}"): no platform workspace has that key`)
  }
  const placement = classifyPlatformWorkspace(workspace)
  if (placement.kind !== 'studio') {
    throw new Error(
      `workspaceCategory("${key}"): "${workspace.to}" is classified as a business destination `
      + `(${placement.reason}) and has no Settings Studio category`,
    )
  }
  return placement.category
}
